import { useState, useEffect } from 'react'
import { Search, Check, X, User, Activity, Heart, Syringe, Stethoscope, Pill, Baby, Brain, Eye, Wind, Thermometer, Scan, CheckCircle2, Loader2, Shield } from 'lucide-react'
import { medicalApi, eventsApi, pointsApi } from '../../lib/api'

const eventIcons: Record<string, React.ElementType> = {
  e1: Activity, e2: Heart, e3: Syringe, e4: Stethoscope, e5: Pill, e6: Baby,
  e7: Brain, e8: Eye, e9: Wind, e10: Thermometer, e11: Scan,
}

const AdminPointEntry = () => {
  const [search, setSearch] = useState('')
  const [users, setUsers] = useState<any[]>([])
  const [events, setEvents] = useState<any[]>([])
  const [selectedUser, setSelectedUser] = useState<any | null>(null)
  const [selectedEventId, setSelectedEventId] = useState('')
  const [successModal, setSuccessModal] = useState(false)
  const [formError, setFormError] = useState('')
  const [loading, setLoading] = useState(true)
  const [searching, setSearching] = useState(false)
  const [submitting, setSubmitting] = useState(false)
  const [hasSearched, setHasSearched] = useState(false)
  const [remark, setRemark] = useState('')
  const [entryTime, setEntryTime] = useState(() => {
    const d = new Date()
    const pad = (n: number) => String(n).padStart(2, '0')
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`
  })
  const [useHistoryTime, setUseHistoryTime] = useState(false)

  // 获取当前管理员账号信息
  const rawAuthUser = localStorage.getItem('auth_user')
  const authUser = rawAuthUser ? JSON.parse(rawAuthUser) : null
  const isSupervisor = authUser?.role === 'medical' && authUser?.medicalRole === 'supervisor'
  const isAdmin = authUser?.role === 'admin'
  const operatorName = authUser?.name || '管理员'
  const hospitalId = authUser?.hospitalId || null

  useEffect(() => {
    const load = async () => {
      try {
        const eventsResp = await eventsApi.list({ is_active: true }) as any
        setEvents((eventsResp.event_types || []).map((e: any) => ({
          id: e.id, name: e.name, description: e.description || '',
          points: e.points, cycle: e.cycle || 'once', cycleLimit: e.cycle_limit || 999,
          enabled: e.enabled,
        })))
      } catch (e) {
        console.error('Failed to load data', e)
      } finally {
        setLoading(false)
      }
    }
    load()
  }, [])

  // 搜索用户
  const handleSearch = async () => {
    if (!search.trim()) return
    setSearching(true)
    setHasSearched(true)
    try {
      const resp = await medicalApi.searchUsers(search.trim(), 20) as any
      setUsers((resp.users || []).map((u: any) => ({
        id: u.id, name: u.name || '未知', phone: u.phone || '', idCard: u.id_card || '',
        points: u.points ?? 0, status: u.status,
        hospital_name: u.hospital_name || '',
        crowd_type: u.crowd_type || '',
        age_group: u.age_group || '',
        tags: u.tags || '',
      })))
    } catch (e: any) {
      console.error('Failed to search users', e)
      setUsers([])
      setFormError(e.message || '搜索失败')
    } finally {
      setSearching(false)
    }
  }

  const availableEvents = events.filter(e => e.enabled)
  const selectedEvent = events.find(e => e.id === selectedEventId)

  const handleSelectUser = (user: any) => {
    setSelectedUser(user)
    setSearch(user.phone)
    setFormError('')
  }

  const handleSubmit = async () => {
    if (!selectedUser) { setFormError('请先选择用户'); return }
    if (!selectedEventId) { setFormError('请选择事件类型'); return }
    setSubmitting(true)
    try {
      await pointsApi.create({
        user_id: selectedUser.id,
        event_type_id: selectedEventId,
        hospital_id: hospitalId || undefined,
        remark: remark.trim() || undefined,
        entry_time: useHistoryTime ? entryTime : undefined,
      })
      setSuccessModal(true)
    } catch (e: any) {
      setFormError(e.message || '录入失败，请重试')
    } finally {
      setSubmitting(false)
    }
  }

  const handleConfirm = () => {
    setSelectedUser(null)
    setSelectedEventId('')
    setSuccessModal(false)
    setFormError('')
    setSearch('')
    setRemark('')
    const d = new Date()
    const pad = (n: number) => String(n).padStart(2, '0')
    setEntryTime(`${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`)
    setUseHistoryTime(false)
  }

  const currentStep = selectedUser ? (selectedEventId ? 3 : 2) : 1

  return (
    <div className="max-w-5xl mx-auto space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-800 flex items-center gap-2">
          积分录入
          {isSupervisor && (
            <span className="flex items-center gap-1 text-sm font-normal text-green-600 bg-green-50 px-2 py-1 rounded-lg">
              <Shield size={14} /> 本机构
            </span>
          )}
        </h1>
        <p className="text-gray-500 text-sm mt-1">
          {isSupervisor ? '为机构内会员添加健康积分，积分值由事件类型自动确定' : '为会员添加健康积分，积分值由事件类型自动确定'}
        </p>
      </div>

      {/* 步骤指示器 */}
      <div className="flex items-center justify-center space-x-0">
        {[{ step: 1, label: '选择用户' }, { step: 2, label: '选择事件' }, { step: 3, label: '确认录入' }].map((s, i) => (
          <div key={s.step} className="flex items-center">
            <div className="flex flex-col items-center">
              <div className={`w-10 h-10 rounded-full flex items-center justify-center font-bold text-sm transition-all ${
                currentStep > s.step ? 'bg-green-600 text-white'
                  : currentStep === s.step ? 'bg-green-700 text-white ring-4 ring-green-100'
                  : 'bg-gray-100 text-gray-400'
              }`}>
                {currentStep > s.step ? <Check className="w-5 h-5" /> : s.step}
              </div>
              <span className={`text-xs mt-1.5 font-medium ${currentStep >= s.step ? 'text-green-700' : 'text-gray-400'}`}>{s.label}</span>
            </div>
            {i < 2 && <div className={`w-20 h-0.5 mx-2 transition-all ${currentStep > s.step ? 'bg-green-400' : 'bg-gray-200'}`} />}
          </div>
        ))}
      </div>

      {loading ? (
        <div className="flex justify-center py-12"><Loader2 className="w-8 h-8 animate-spin text-green-600" /></div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className={`space-y-4 ${currentStep !== 3 ? 'lg:col-span-3' : 'lg:col-span-1'}`}>
            <div className={`bg-white rounded-2xl shadow-md border border-gray-100 ${currentStep === 1 ? 'p-8' : 'p-6'}`}>
              <h2 className="font-bold text-gray-700 mb-3 flex items-center text-sm">
                <User className="w-4 h-4 mr-2 text-green-600" />
                {currentStep > 1 ? '已选用户' : '搜索用户'}
              </h2>

              {currentStep === 1 ? (
                <>
                  <div className="flex space-x-3 mb-6">
                    <div className="relative flex-1">
                      <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                      <input type="text" placeholder="输入姓名、手机号或身份证搜索用户"
                        value={search} onChange={(e) => { setSearch(e.target.value); setSelectedUser(null) }}
                        onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
                        className="w-full pl-12 pr-4 py-4 text-base border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-green-500 focus:border-green-400 transition-all bg-gray-50 focus:bg-white" />
                    </div>
                    <button onClick={handleSearch} disabled={searching || !search.trim()}
                      className={`px-6 py-4 rounded-xl font-medium transition-all flex items-center space-x-2 ${
                        searching || !search.trim() ? 'bg-gray-200 text-gray-400' : 'bg-green-600 text-white hover:bg-green-700'
                      }`}>
                      {searching ? <Loader2 className="w-5 h-5 animate-spin" /> : <Search className="w-5 h-5" />}
                      <span>搜索</span>
                    </button>
                  </div>
                  {formError && (
                    <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-xl text-red-600 text-sm flex items-center space-x-2">
                      <X className="w-4 h-4 shrink-0" /><span>{formError}</span>
                    </div>
                  )}
                  {hasSearched && (
                    <div className="border-2 border-gray-100 rounded-2xl divide-y divide-gray-50 max-h-80 overflow-y-auto bg-gray-50">
                      {users.length === 0 ? (
                        <div className="p-8 text-center text-gray-400 text-base">未找到匹配的用户</div>
                      ) : (
                        users.slice(0, 6).map((u) => (
                          <button key={u.id} onClick={() => handleSelectUser(u)}
                            className="w-full flex items-center space-x-4 p-4 hover:bg-green-50 transition-colors text-left">
                            <div className="w-12 h-12 bg-green-600 rounded-full flex items-center justify-center flex-shrink-0 shadow-sm">
                              <span className="text-white font-bold text-lg">{u.name.charAt(0)}</span>
                            </div>
                            <div className="flex-1 min-w-0">
                              <p className="font-semibold text-gray-800">{u.name}</p>
                              <p className="text-sm text-gray-400">{u.phone} · {u.idCard ? '***' + u.idCard.slice(-4) : ''}</p>
                              {(u.crowd_type || u.age_group || u.tags) && (
                                <p className="text-xs text-gray-500 mt-0.5">
                                  {u.crowd_type && <span className="inline-block bg-blue-50 text-blue-600 px-1.5 py-0.5 rounded mr-1">{u.crowd_type}</span>}
                                  {u.age_group && <span className="inline-block bg-purple-50 text-purple-600 px-1.5 py-0.5 rounded mr-1">{u.age_group}</span>}
                                  {u.tags && u.tags.split(',').map((tag: string, i: number) => tag.trim() && (
                                    <span key={i} className="inline-block bg-gray-100 text-gray-500 px-1.5 py-0.5 rounded mr-1">{tag.trim()}</span>
                                  ))}
                                </p>
                              )}
                            </div>
                            <div className="text-right shrink-0">
                              <span className="inline-block bg-green-100 text-green-700 font-bold px-3 py-1.5 rounded-full">+{u.points}分</span>
                            </div>
                          </button>
                        ))
                      )}
                    </div>
                  )}
                </>
              ) : (
                <div className="flex items-center space-x-3 p-3 bg-green-50 rounded-xl">
                  <div className="w-12 h-12 bg-green-600 rounded-full flex items-center justify-center flex-shrink-0">
                    <span className="text-white font-bold text-lg">{selectedUser!.name.charAt(0)}</span>
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-bold text-gray-800">{selectedUser!.name}</p>
                    <p className="text-xs text-gray-400">{selectedUser!.phone} · 当前积分 {selectedUser!.points}</p>
                  </div>
                  <button onClick={() => { setSelectedUser(null); setSelectedEventId(''); setSearch('') }}
                    className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors">
                    <X className="w-4 h-4" />
                  </button>
                </div>
              )}
            </div>

            {currentStep >= 2 && (
              <div className="bg-white rounded-2xl shadow-md p-6">
                <h2 className="font-bold text-gray-700 mb-4 flex items-center text-base">
                  <Activity className="w-5 h-5 mr-2 text-green-600" />
                  {currentStep === 3 ? '已选事件' : '选择事件类型'}
                </h2>
                {currentStep === 2 ? (
                  <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 max-h-none overflow-y-auto">
                    {availableEvents.map((e) => {
                      const Icon = eventIcons[e.id] || Activity
                      const isSelected = selectedEventId === e.id
                      return (
                        <button key={e.id} onClick={() => setSelectedEventId(e.id)}
                          className={`flex flex-col items-center space-y-3 p-5 rounded-2xl border-2 transition-all text-center ${
                            isSelected ? 'border-green-600 bg-green-50 shadow-md shadow-green-100' : 'border-gray-100 hover:border-green-300 hover:bg-green-50/50'
                          }`}>
                          <div className={`w-14 h-14 rounded-2xl flex items-center justify-center flex-shrink-0 shadow-sm ${isSelected ? 'bg-green-600' : 'bg-gray-100'}`}>
                            <Icon className={`w-7 h-7 ${isSelected ? 'text-white' : 'text-gray-500'}`} />
                          </div>
                          <div className="w-full min-w-0">
                            <p className={`text-sm font-semibold truncate ${isSelected ? 'text-green-700' : 'text-gray-700'}`}>{e.name}</p>
                            <p className={`text-base font-black mt-0.5 ${isSelected ? 'text-green-600' : 'text-gray-400'}`}>+{e.points}分</p>
                          </div>
                          {isSelected && <Check className="w-3.5 h-3.5 text-green-600 shrink-0" />}
                        </button>
                      )
                    })}
                  </div>
                ) : (
                  selectedEvent && (() => {
                    const Icon = eventIcons[selectedEvent.id] || Activity
                    return (
                      <div className="flex items-center space-x-3 p-3 border-2 border-green-200 bg-green-50 rounded-xl">
                        <div className="w-12 h-12 bg-green-600 rounded-xl flex items-center justify-center flex-shrink-0">
                          <Icon className="w-5 h-5 text-white" />
                        </div>
                        <div className="flex-1">
                          <p className="font-bold text-gray-800">{selectedEvent.name}</p>
                          <p className="text-xs text-gray-500">{selectedEvent.description}</p>
                        </div>
                        <div className="text-right">
                          <p className="text-2xl font-black text-green-600">+{selectedEvent.points}</p>
                          <p className="text-xs text-gray-400">积分</p>
                        </div>
                      </div>
                    )
                  })()
                )}
              </div>
            )}
          </div>

          {currentStep === 3 && selectedUser && selectedEvent && (
            <div className="space-y-4 lg:col-span-2">
              <div className="bg-white rounded-2xl shadow-md p-5">
                <h2 className="font-bold text-gray-700 mb-4 flex items-center text-sm">
                  <CheckCircle2 className="w-4 h-4 mr-2 text-green-600" />确认信息
                </h2>
                {formError && (
                  <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-xl text-red-600 text-sm flex items-center space-x-2">
                    <X className="w-4 h-4 shrink-0" /><span>{formError}</span>
                  </div>
                )}
                <div className="space-y-3 mb-5">
                  <div className="flex items-center justify-between py-2.5 border-b border-gray-100">
                    <span className="text-sm text-gray-500">用户姓名</span><span className="font-medium text-gray-800">{selectedUser.name}</span>
                  </div>
                  <div className="flex items-center justify-between py-2.5 border-b border-gray-100">
                    <span className="text-sm text-gray-500">手机号</span><span className="text-gray-700">{selectedUser.phone}</span>
                  </div>
                  <div className="flex items-center justify-between py-2.5 border-b border-gray-100">
                    <span className="text-sm text-gray-500">身份证</span><span className="text-gray-700 text-xs">{selectedUser.idCard}</span>
                  </div>
                  <div className="flex items-center justify-between py-2.5 border-b border-gray-100">
                    <span className="text-sm text-gray-500">当前积分</span><span className="font-medium text-gray-800">{selectedUser.points}</span>
                  </div>
                  <div className="flex items-center justify-between py-2.5 border-b border-gray-100">
                    <span className="text-sm text-gray-500">所属机构</span><span className="text-gray-700">{selectedUser.hospital_name || '-'}</span>
                  </div>
                  {selectedUser.crowd_type && (
                    <div className="flex items-center justify-between py-2.5 border-b border-gray-100">
                      <span className="text-sm text-gray-500">人群类型</span><span className="text-gray-700">{selectedUser.crowd_type}</span>
                    </div>
                  )}
                  {selectedUser.age_group && (
                    <div className="flex items-center justify-between py-2.5 border-b border-gray-100">
                      <span className="text-sm text-gray-500">年龄段</span><span className="text-gray-700">{selectedUser.age_group}</span>
                    </div>
                  )}
                  {selectedUser.tags && (
                    <div className="flex items-center justify-between py-2.5 border-b border-gray-100">
                      <span className="text-sm text-gray-500">标签</span>
                      <span className="text-gray-700">
                        {selectedUser.tags.split(',').map((tag: string, i: number) => tag.trim() && (
                          <span key={i} className="inline-block bg-gray-100 text-gray-600 px-1.5 py-0.5 rounded text-xs mr-1">{tag.trim()}</span>
                        ))}
                      </span>
                    </div>
                  )}
                  <div className="flex items-center justify-between py-2.5 border-b border-gray-100">
                    <span className="text-sm text-gray-500">事件类型</span><span className="text-gray-700">{selectedEvent.name}</span>
                  </div>
                  <div className="flex items-center justify-between py-2.5 border-b border-gray-100">
                    <span className="text-sm text-gray-500">积分周期</span>
                    <span className="text-gray-700 text-xs">
                      {({day:'每天',week:'每周',month:'每月',quarter:'每季度',year:'每年'} as Record<string,string>)[selectedEvent.cycle] || '一次性'}
                      （上限{selectedEvent.cycleLimit}次）
                    </span>
                  </div>
                  <div className="flex items-center justify-between py-3 bg-green-50 rounded-xl px-3 mt-2">
                    <span className="font-medium text-green-700">本次添加积分</span>
                    <span className="text-2xl font-black text-green-600">+{selectedEvent.points}</span>
                  </div>
                  <div className="flex items-center justify-between py-2.5 border-t border-gray-100">
                    <span className="text-sm text-gray-500">操作人</span>
                    <span className="text-gray-700">{operatorName}</span>
                  </div>
                  <div className="border-t border-gray-100 pt-3 mt-3">
                    <div className="flex items-center justify-between mb-2">
                      <label className="text-sm text-gray-500">录入时间</label>
                      <label className="inline-flex items-center gap-2 text-sm text-gray-600 cursor-pointer">
                        <input type="checkbox" checked={useHistoryTime} onChange={e => setUseHistoryTime(e.target.checked)} className="w-4 h-4 accent-green-600" />
                        指定历史时间
                      </label>
                    </div>
                    {useHistoryTime ? (
                      <>
                        <input type="datetime-local" value={entryTime} onChange={e => setEntryTime(e.target.value)} className="w-full px-3 py-2 border border-amber-300 rounded-xl text-sm focus:outline-none focus:border-amber-400 bg-amber-50" />
                        <p className="text-xs text-amber-600 mt-1">周期限制和重复判断将按此历史时间计算</p>
                      </>
                    ) : (
                      <p className="text-gray-700 text-sm">当前时间（提交时自动记录）</p>
                    )}
                  </div>
                  <div className="border-t border-gray-100 pt-3 mt-3">
                    <label className="text-sm text-gray-500 mb-1 block">备注（可选）</label>
                    <textarea
                      value={remark}
                      onChange={e => setRemark(e.target.value.slice(0, 500))}
                      placeholder="可填写补充说明，如特殊情况说明等"
                      rows={2}
                      className="w-full px-3 py-2 border border-gray-200 rounded-xl text-sm focus:outline-none resize-none focus:border-green-400"
                    />
                  </div>
                </div>
                <button onClick={handleSubmit} disabled={submitting}
                  className={`w-full py-4 rounded-2xl font-bold text-lg transition-all ${submitting ? 'bg-gray-300 text-gray-500 cursor-not-allowed' : 'bg-green-700 text-white hover:bg-green-800 active:scale-[0.98]'}`}>
                  {submitting ? <><Loader2 className="w-5 h-5 animate-spin inline mr-2" />提交中...</> : '确认录入 (+' + selectedEvent.points + '积分)'}
                </button>
              </div>
            </div>
          )}
        </div>
      )}

      {/* 成功弹窗 */}
      {successModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="bg-white rounded-2xl w-full max-w-sm p-6 text-center">
            <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <CheckCircle2 className="w-8 h-8 text-green-600" />
            </div>
            <h3 className="text-lg font-bold text-gray-800 mb-2">积分已到账</h3>
            <p className="text-sm text-gray-500 mb-4">积分事件录入成功，积分已实时计入该用户账户</p>
            <button onClick={handleConfirm}
              className="w-full py-3 bg-green-700 text-white rounded-xl font-medium hover:bg-green-800">
              继续录入
            </button>
          </div>
        </div>
      )}
    </div>
  )
}

export default AdminPointEntry
